import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter, useSearchParams } from 'next/navigation';
import type React from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { AuthLink, AuthScreen, Button, InputField } from 'src/components/DS';
import ToasterComponent from 'src/components/Shared/ToasterComponent';
import { useAuth } from 'src/context/AuthContext';
import useStatus from 'src/hooks/useStatus';
import { AUTH_ROUTES, COORDINATOR_ROUTES, VENDOR_ROUTES } from 'src/shared/constants/routes';
import { checkVendorDomain } from 'src/shared/utils/hosts';
import { safeInternalRedirect } from 'src/shared/utils/redirects';
import * as yup from 'yup';
import SocialSignInButtons from './components/SocialSignInButtons';
import { AUTH_FORM_VALIDATION, LOGIN_PAGE } from './constants';

type SignInFormInputs = {
  email: string;
  password: string;
};

/** Sign-in failures the inline alert already words as "Incorrect email or password". */
const CREDENTIAL_FAILURE_CODES = new Set([
  'UserNotFoundException',
  'NotAuthorizedException',
  'UserLambdaValidationException',
]);

const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email(AUTH_FORM_VALIDATION.EMAIL_INVALID)
    .required(AUTH_FORM_VALIDATION.EMAIL_REQUIRED),
  password: yup.string().required(AUTH_FORM_VALIDATION.PASSWORD_REQUIRED),
});

const Login: React.FC = () => {
  const auth = useAuth();
  const {
    loading,
    success,
    successMessage,
    errorMessage,
    showMessage,
    setLoading,
    setError,
    setSuccess,
    resetState,
  } = useStatus('login');
  const router = useRouter();
  const searchParams = useSearchParams();

  const methods = useForm<SignInFormInputs>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    resetState();
    // An authenticated account that the backend rejects (e.g. an account without
    // access to this portal) is bounced here by the global 401 handler. Surface a
    // message instead of leaving the user on a silently-reset login form.
    const unauthorizedAccess = sessionStorage.getItem('unauthorizedAccess');
    if (unauthorizedAccess === 'true') {
      sessionStorage.removeItem('unauthorizedAccess');
      setError(LOGIN_PAGE.UNAUTHORIZED_ACCESS_MESSAGE);
      return;
    }
    // Check if user was redirected due to session expiration
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    if (sessionExpired === 'true') {
      sessionStorage.removeItem('sessionExpired');
      setError(LOGIN_PAGE.SESSION_EXPIRED_MESSAGE);
    }
  }, [resetState, setError]);

  const { handleSubmit } = methods;

  const onSubmit = async (data: SignInFormInputs) => {
    setLoading();
    const result = await auth.signIn(data?.email, data?.password);
    const rawRedirectUrl = sessionStorage.getItem('redirectUrl') || searchParams.get('redirectUrl');
    const redirectUrl = safeInternalRedirect(rawRedirectUrl);
    if (result.success) {
      const isVendor = checkVendorDomain();
      setSuccess(LOGIN_PAGE.SUCCESS_MESSAGE);
      if (result.navigate === 'set-new-password') {
        router.push(AUTH_ROUTES.NEW_PASSWORD);
      } else if (result.navigate === 'mfa-verification') {
        router.push(AUTH_ROUTES.MFA_VERIFICATION);
      } else if (result.navigate === 'sms-mfa-verification') {
        router.push(AUTH_ROUTES.SMS_MFA_VERIFICATION);
      } else if (redirectUrl) {
        // Mark the post-login window: if the backend rejects this authenticated
        // account with a 401 while loading the destination, the global 401 handler
        // treats it as an unauthorized account rather than a session expiry.
        sessionStorage.setItem('postLoginVerification', 'true');
        sessionStorage.removeItem('redirectUrl');
        globalThis.location.href = redirectUrl;
      } else {
        sessionStorage.setItem('postLoginVerification', 'true');
        // Evict a REJECTED candidate here and NOWHERE EARLIER. sessionStorage wins the `||` above,
        // so a value this guard refuses would otherwise shadow every later legitimate proxy-minted
        // `?redirectUrl=` for the rest of the tab session. Do NOT hoist above the branch chain: the
        // three `result.navigate` branches deliberately leave the value in place so the MFA /
        // set-new-password round-trip (MFAVerification.tsx:43 -> app/page.tsx:15 -> /login) can
        // resume it at AuthLayout. Scope note: the two failure branches below deliberately do not
        // evict either, so a rejected value still survives a FAILED login attempt — AuthLayout
        // evicts it on the next authenticated mount. [APX-387]
        sessionStorage.removeItem('redirectUrl');
        router.push(isVendor ? VENDOR_ROUTES.HOME : COORDINATOR_ROUTES.HOME);
      }
    } else if (result?.code === 'UserNotConfirmedException') {
      sessionStorage.setItem(
        'emailVerificationState',
        JSON.stringify({ userId: result?.username }),
      );
      router.push(AUTH_ROUTES.EMAIL_VERIFICATION);
    } else {
      if (result?.code === 'NetworkError') {
        setError(LOGIN_PAGE.ERROR_SOMETHING_WENT_WRONG);
      } else {
        setError(LOGIN_PAGE.ERROR_MESSAGE);
      }
      // The inline alert already says "Incorrect email or password" for these, so a toast saying
      // "Invalid Credentials" only repeats it. Every other failure keeps its toast, with whatever
      // message the auth context gives it. That is not always a distinct reason: Cognito reports a
      // disabled account or too many attempts as NotAuthorizedException, which AuthContext words as
      // "Invalid Credentials" too, so those users see the alert only.
      if (!CREDENTIAL_FAILURE_CODES.has(result?.code ?? '')) {
        toast(
          <ToasterComponent
            type="error"
            icon="error"
            description={result?.message || 'Something Went Wrong'}
          />,
        );
      }
    }
  };

  // "The organization not found." used to be a red line under Log in; it belongs to the whole
  // form, so it now rides the card's alert like the other form-level messages.
  let alert: { type: 'success' | 'error'; message: string } | null = null;
  if (auth?.domainNotValid) {
    alert = { type: 'error', message: LOGIN_PAGE.ORGANIZATION_NOT_FOUND_MESSAGE };
  } else if (showMessage) {
    alert = success
      ? { type: 'success', message: successMessage }
      : { type: 'error', message: errorMessage };
  }

  return (
    <FormProvider {...methods}>
      <AuthScreen
        title={LOGIN_PAGE.TITLE}
        alert={alert}
        onSubmit={success ? undefined : handleSubmit(onSubmit)}
        data-testid={success ? 'auth-card' : 'auth.login-form'}
      >
        {!success && (
          <>
            <SocialSignInButtons providers={auth.authConfigDetails.providers} />
            <InputField
              type="text"
              placeholder=""
              name="email"
              label="Email Address"
              data-testid="email-input"
            />
            <InputField
              type="password"
              placeholder=""
              name="password"
              label="Password"
              data-testid="password-input"
            />
            <div>
              <AuthLink href={AUTH_ROUTES.FORGOT_PASSWORD} data-testid="auth.forgot-password-link">
                {LOGIN_PAGE.LINK_LABEL}
              </AuthLink>
            </div>
            <Button
              type="submit"
              block
              disabled={loading || !auth.isAuthInitialized}
              isLoading={loading}
              data-testid="auth.login-submit"
            >
              {LOGIN_PAGE.SUBMIT_LABEL}
            </Button>
          </>
        )}
      </AuthScreen>
    </FormProvider>
  );
};

export default Login;
